import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@ohpen/enso-ui';

import { ConfigTableActions } from '@config/components';
import { CONFIG_PAGINATION, CONFIG_SCROLL } from '@config/utils';
import type { TranslationRow } from '../interfaces';
import { AREA_LIST_NAME, translationColumns } from '../tableConfig';
import { flatten, HOST_NAMESPACE } from '../utils';

export const TranslationsList = (): ReactElement => {
  const { t, i18n: instance, ready } = useTranslation();

  // Read through the hook's instance and only once it reports the namespace loaded. Reading the
  // global one early flattens nothing and, keyed on the language alone, never recomputes.
  const bundle = ready ? instance.getResourceBundle(instance.resolvedLanguage ?? 'en', HOST_NAMESPACE) : undefined;

  const dataSource: TranslationRow[] = useMemo(() => {
    const rows: TranslationRow[] = [];
    flatten(bundle, '', rows);
    return rows.sort((a, b) => a.key.localeCompare(b.key));
  }, [bundle]);

  const valueLists = useMemo(() => {
    const areas: Record<string, string> = {};
    dataSource.forEach(row => {
      areas[row.area] = row.area;
    });

    return { [AREA_LIST_NAME]: areas };
  }, [dataSource]);

  return (
    <Table
      id="configTranslations"
      rowKey="key"
      dataSource={dataSource}
      columns={translationColumns}
      valueLists={valueLists}
      loading={!ready}
      pagination={CONFIG_PAGINATION}
      scroll={CONFIG_SCROLL}
      itemsLabel={t('config.sections.translations')}
    >
      <ConfigTableActions />
    </Table>
  );
};
